import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Card, Button, ProgressBar, Alert, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, 
  faArrowRight, 
  faSignature, 
  faCheckCircle,
  faFileContract,
  faMobileAlt
} from '@fortawesome/free-solid-svg-icons';
import SignatureCanvas from 'react-signature-canvas';
import { Document, Page, pdfjs } from 'react-pdf';
import axiosInstance from '../../helpers/api';
import { toast } from 'react-toastify';
import './CustomerDocumentSign.scss';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

interface SignatureField {
  id: string;
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
  type: 'signature' | 'initial' | 'date' | 'text';
  required: boolean;
  completed: boolean;
  value?: string;
}

const CustomerDocumentSign: React.FC = () => {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const signaturePadRef = useRef<SignatureCanvas>(null);
  
  const [loading, setLoading] = useState(true);
  const [document, setDocument] = useState<any>(null);
  const [pdfUrl, setPdfUrl] = useState('');
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [fields, setFields] = useState<SignatureField[]>([]);
  const [currentFieldIndex, setCurrentFieldIndex] = useState(0);
  const [signature, setSignature] = useState('');
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    fetchDocument();
    
    // Handle resize for mobile optimization
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [documentId]);

  const fetchDocument = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/customer-portal/documents/${documentId}/signing`);
      
      if (response.data.success) {
        setDocument(response.data.document);
        setPdfUrl(response.data.pdfUrl);
        setFields(response.data.fields);
        
        // Jump to first incomplete field
        const firstIncomplete = response.data.fields.findIndex((f: SignatureField) => !f.completed);
        if (firstIncomplete > -1) {
          setCurrentFieldIndex(firstIncomplete);
          setCurrentPage(response.data.fields[firstIncomplete].page);
        }
      }
    } catch (error) {
      console.error('Error fetching document:', error);
      toast.error('Failed to load document');
      navigate('/customer-portal/documents');
    } finally {
      setLoading(false);
    }
  };

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  };

  const currentField = fields[currentFieldIndex];
  const progress = (fields.filter(f => f.completed).length / fields.length) * 100;

  const handleSignature = () => {
    if (signaturePadRef.current) {
      const signatureData = signaturePadRef.current.toDataURL();
      setSignature(signatureData);
      
      // Update field as completed
      const updatedFields = [...fields];
      updatedFields[currentFieldIndex] = {
        ...updatedFields[currentFieldIndex],
        completed: true,
        value: signatureData
      };
      setFields(updatedFields);
      
      // Close modal and go to next field
      setShowSignatureModal(false);
      nextField();
    }
  };

  const clearSignature = () => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
    }
  };

  const nextField = () => {
    const nextIncomplete = fields.findIndex((f, idx) => idx > currentFieldIndex && !f.completed);
    if (nextIncomplete > -1) {
      setCurrentFieldIndex(nextIncomplete);
      setCurrentPage(fields[nextIncomplete].page);
    } else {
      // All fields completed, check if we can submit
      if (fields.every(f => !f.required || f.completed)) {
        completeSigningProcess();
      }
    }
  };

  const previousField = () => {
    if (currentFieldIndex > 0) {
      setCurrentFieldIndex(currentFieldIndex - 1);
      setCurrentPage(fields[currentFieldIndex - 1].page);
    }
  };

  const completeSigningProcess = async () => {
    try {
      const response = await axiosInstance.post(`/customer-portal/documents/${documentId}/complete-signing`, {
        fields: fields.map(f => ({
          id: f.id,
          value: f.value
        }))
      });
      
      if (response.data.success) {
        toast.success('Document signed successfully!');
        
        // Show success screen briefly then redirect
        setTimeout(() => {
          navigate('/customer-portal/documents');
        }, 2000);
      }
    } catch (error) {
      console.error('Error completing signing:', error);
      toast.error('Failed to complete signing');
    }
  };

  if (loading) {
    return (
      <Container className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </Container>
    );
  }

  return (
    <Container className="document-signing py-3">
        {/* Header with Progress */}
        <div className="signing-header mb-3">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <Button 
              variant="link" 
              onClick={() => navigate('/customer-portal/documents')}
              className="text-decoration-none"
            >
              <FontAwesomeIcon icon={faArrowLeft} /> Back
            </Button>
            
            <h5 className="mb-0">{document?.name}</h5>
            
            <div className="text-muted">
              {currentFieldIndex + 1} of {fields.length}
            </div>
          </div>
          
          <ProgressBar 
            now={progress} 
            label={`${Math.round(progress)}%`}
            className="signing-progress"
          />
        </div>

        {/* Mobile-Optimized Alert */}
        {isMobile && currentField?.type === 'signature' && (
          <Alert variant="info" className="mobile-tip">
            <FontAwesomeIcon icon={faMobileAlt} /> Rotate your device for easier signing
          </Alert>
        )}

        {/* Document Viewer */}
        <Card className="document-viewer-card">
          <Card.Body className="p-0">
            <div className="pdf-container">
              <Document
                file={pdfUrl}
                onLoadSuccess={onDocumentLoadSuccess}
                className="pdf-document"
              >
                <Page 
                  pageNumber={currentPage} 
                  width={isMobile ? window.innerWidth - 40 : 600}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                />
                
                {/* Highlight current field */}
                {currentField && currentField.page === currentPage && (
                  <div 
                    className="field-highlight"
                    style={{
                      left: `${currentField.x}%`,
                      top: `${currentField.y}%`,
                      width: `${currentField.width}%`,
                      height: `${currentField.height}%`
                    }}
                  >
                    <div className="field-indicator">
                      <FontAwesomeIcon icon={faSignature} />
                      {currentField.type === 'signature' ? ' Sign Here' : ' Fill Here'}
                    </div>
                  </div>
                )}
              </Document>
            </div>
          </Card.Body>
        </Card>

        {/* Action Buttons */}
        <div className="signing-actions mt-3">
          <Button 
            variant="outline-secondary" 
            onClick={previousField}
            disabled={currentFieldIndex === 0}
          >
            <FontAwesomeIcon icon={faArrowLeft} /> Previous
          </Button>
          
          {currentField?.type === 'signature' ? (
            <Button 
              variant="primary" 
              onClick={() => setShowSignatureModal(true)}
              className="mx-2"
            >
              <FontAwesomeIcon icon={faSignature} /> Add Signature
            </Button>
          ) : (
            <Button 
              variant="primary" 
              onClick={nextField}
              className="mx-2"
            >
              Next <FontAwesomeIcon icon={faArrowRight} />
            </Button>
          )}
          
          {fields.every(f => !f.required || f.completed) && (
            <Button 
              variant="success" 
              onClick={completeSigningProcess}
            >
              <FontAwesomeIcon icon={faCheckCircle} /> Complete
            </Button>
          )}
        </div>

        {/* Signature Modal */}
        <Modal 
          show={showSignatureModal} 
          onHide={() => setShowSignatureModal(false)}
          size="lg"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Add Your Signature</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="signature-pad-container">
              <SignatureCanvas
                ref={signaturePadRef}
                penColor="black"
                canvasProps={{
                  className: 'signature-canvas'
                }}
              />
            </div>
            <div className="text-center mt-3">
              <Button 
                variant="link" 
                onClick={clearSignature}
                className="text-muted"
              >
                Clear
              </Button>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button 
              variant="secondary" 
              onClick={() => setShowSignatureModal(false)}
            >
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={handleSignature}
            >
              Apply Signature
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
  );
};

export default CustomerDocumentSign;